import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import './App.css';

// Import components
import Header from './components/Header';
import HomePage from './pages/HomePage';
import CategoryPage from './pages/CategoryPage';
import GamePage from './pages/GamePage';
import SearchPage from './pages/SearchPage';
import DynamicFavicon from './components/DynamicFavicon';

// Predefined image files as fallback
const defaultImageFiles = [
  '/gameImages/8 Ball Pool_icon.png',
  '/gameImages/Albion Online_icon.png',
  '/gameImages/Among Us_icon.png',
  '/gameImages/Angry Birds 2_icon.png',
  '/gameImages/Archero_icon.png',
  '/gameImages/Asphalt 8 - Car Racing Game_icon.png',
  '/gameImages/Brawl Stars_icon.png',
  '/gameImages/Candy Crush Saga_icon.png',
  '/gameImages/Clash of Clans_icon.png',
  '/gameImages/Coin Master_icon.png',
  '/gameImages/Dead Cells_icon.png',
  '/gameImages/Diablo Immortal_icon.png',
  '/gameImages/Dragon Mania Legends_icon.png',
  '/gameImages/Eternium_icon.png',
  '/gameImages/Fallout Shelter_icon.png',
  '/gameImages/Fruit Ninja Classic_icon.png',
  '/gameImages/Gardenscapes_icon.png',
  '/gameImages/Genshin Impact_icon.png',
  '/gameImages/Geometry Dash_icon.png',
  '/gameImages/Guardian Tales_icon.png',
  '/gameImages/Hay Day_icon.png',
  '/gameImages/Hill Climb Racing_icon.png',
  '/gameImages/Homescapes_icon.png',
  '/gameImages/Jetpack Joyride_icon.png',
  '/gameImages/PUBG MOBILE_icon.png',
  '/gameImages/Plants vs Zombies™ 2_icon.png',
  '/gameImages/Pokémon GO_icon.png',
  '/gameImages/Portal Knights_icon.png',
  '/gameImages/Real Racing 3_icon.png',
  '/gameImages/Shadow Fight 2_icon.png',
  '/gameImages/SimCity BuildIt_icon.png',
  '/gameImages/Soul Knight_icon.png',
  '/gameImages/Subway Surfers_icon.png',
  '/gameImages/Temple Run 2_icon.png',
  '/gameImages/Terraria_icon.png',
  '/gameImages/The Room_icon.png'
];

function App() {
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gameImages, setGameImages] = useState([]);



  // First effect: Load the game images
  useEffect(() => {
    // Attempt to fetch image list or use defaults
    setGameImages(defaultImageFiles);
  }, []);

  // Second effect: Load and process the game data once we have images
  useEffect(() => {
    // Only proceed if we have images loaded
    if (gameImages.length === 0) return;
    
    import('./data3.json')
      .then(data => {
        const categorizedData = {};
        
        // Data3.json already has categories organized
        Object.keys(data.default.categories).forEach(categoryKey => {
          const games = data.default.categories[categoryKey];
          categorizedData[categoryKey] = [];
          
          games.forEach(game => {
            // Use the category-specific image from public/images directory
            const imagePath = `/images/${categoryKey}/${game.id}.png`;
            // Fallback to random image if specific image doesn't exist
            const fallbackImage = gameImages[Math.floor(Math.random() * gameImages.length)];
            
            // Map data3.json structure to the app's expected structure
            categorizedData[categoryKey].push({
              id: game.id,
              name: game.name,
              nameid: game.name.replace(/ /g, '-'),
              file: `${game.id}.html`,
              tags: game.category,
              desc: game.description,
              control: 'Mouse or touch to play',
              ratings: '4.8',
              size: '45 MB',
              developer: 'GameHub',
              current_version: '1.0.0',
              update: '2025-06-06',
              thumb300x300: imagePath,
              thumb512x384: fallbackImage, // Use fallback as alternate image source
            });
          });
        });
        setGameData(categorizedData);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading game data:', err);
        setError('Failed to load game data. Please try again later.');
        setLoading(false);
      });
  }, [gameImages]);

  if (loading) return <div>Loading games...</div>;
  if (error) return <div>{error}</div>;

  return (
    <Router>
      <DynamicFavicon />
      <AppContent gameData={gameData} />
    </Router>
  );
}

// Component to handle conditional header rendering
const AppContent = ({ gameData }) => {
  const location = useLocation();
  const isGamePage = location.pathname.includes('/game/');
  
  return (
    <AppContainer>
      {/* Only show header when not on a game page */}
      {!isGamePage && <Header gameData={gameData} />}
      <Routes>
        <Route path="/" element={<HomePage gameData={gameData} />} />
        <Route path="/category/:categoryName" element={<CategoryPage gameData={gameData} />} />
        <Route path="/game/:categoryName/:gameId" element={<GamePage gameData={gameData} />} />
        <Route path="/search" element={<SearchPage gameData={gameData} />} />
      </Routes>
    </AppContainer>
  );
};

const AppContainer = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 2px;
`;

export default App;
